import { Typography } from '@ace-clublabs/shared-component-library';
import { useGlobalState } from '../../state/state';
import { CurrentPayPlan, RoleType } from '@ace-clublabs/common-membership-api-types';
import { validateEmail } from '../../utils/stringHelper';

export const ConfirmationTitle: React.FC = () => {
  const [confirmation] = useGlobalState('confirmation');
  const [memberDetails] = useGlobalState('memberDetails');
  const { paymentDetails } = confirmation!;
  const { isScheduledPayment, scheduledPaymentDate } = paymentDetails!;
  const { inRenewal, loggedInMember, members } = memberDetails;
  const { roleType, emailAddress: loggedInMemberEmailAddress } = loggedInMember!;

  const primaryEmailAddress = members?.find((member) => member.roleType === RoleType.PRIMARY)?.emailAddress;

  const enrolledInAutoPay =
    confirmation?.updatedBillPlan !== CurrentPayPlan.RECURRINGMONTHLY &&
    confirmation?.paymentDetails?.isAutoPayRequested &&
    confirmation?.paymentDetails?.autoPaySuccessful;

  const getThankYouMessage = () => {
    let thankYouMessage = `Thank you for ${inRenewal ? 'renewing' : 'updating'} your membership`;
    let autoPayMessage = ' and enrolling in Auto Pay';

    if (enrolledInAutoPay) return thankYouMessage + autoPayMessage;

    return thankYouMessage;
  };

  const getOneTimeScheduledPaymentMessage = () => {
    const oneTimeScheduledPaymentMessage = isScheduledPayment
      ? `Your one-time payment has been scheduled for ${scheduledPaymentDate}. `
      : '';

    return oneTimeScheduledPaymentMessage;
  };

  const getEmailSentMessage = (
    loggedInMemberEmailAddress: string | undefined,
    primaryEmailAddress?: string | undefined
  ) => {
    if (validateEmail(loggedInMemberEmailAddress) && validateEmail(primaryEmailAddress)) {
      return (
        <>
          We've sent an email confirmation to{' '}
          <strong className="email-address" data-qm-encrypt="true">
            {primaryEmailAddress}
          </strong>{' '}
          and <strong>{loggedInMemberEmailAddress}</strong>.
        </>
      );
    } else if (validateEmail(loggedInMemberEmailAddress)) {
      return (
        <>
          We've sent an email confirmation to{' '}
          <strong className="email-address" data-qm-encrypt="true">
            {loggedInMemberEmailAddress}
          </strong>
          .
        </>
      );
    } else if (validateEmail(primaryEmailAddress)) {
      return (
        <>
          We've sent an email confirmation to{' '}
          <strong className="email-address" data-qm-encrypt="true">
            {primaryEmailAddress}
          </strong>
          .
        </>
      );
    } else {
      return <></>;
    }
  };

  const getEmailConfirmationMessage = () => {
    if (roleType === RoleType.PRIMARY) {
      return (
        <>
          {getOneTimeScheduledPaymentMessage()}
          {getEmailSentMessage(loggedInMemberEmailAddress)}
        </>
      );
    }

    if ([RoleType.ADULT_ASSOCIATE, RoleType.ASSOCIATE].includes(roleType)) {
      return (
        <>
          {getOneTimeScheduledPaymentMessage()}
          {getEmailSentMessage(loggedInMemberEmailAddress, primaryEmailAddress)}
        </>
      );
    }
  };

  return (
    <>
      <Typography variant="h5" className="confirmation-title" sx={{ fontWeight: 500, margin: '1rem 0 0.5rem 0' }}>
        {getThankYouMessage()}
      </Typography>
      <Typography variant="b2Regular" className="email-confirmation-message">
        {getEmailConfirmationMessage()}
      </Typography>
    </>
  );
};
